import {
  computed,
  ComputedRef,
  MaybeRef,
  MaybeRefOrGetter,
  Ref,
  ref,
  toValue,
  unref,
  UnwrapRef,
  watchEffect,
} from 'vue';
import {
  getRevisedInventionTheme,
  PatentFtoClaimResultVerificationListResponse,
  PatentFtoRevisedInventionTheme,
} from '@/api/patentFto';
import axios, { AxiosError } from 'axios';
import dayjs, { Dayjs } from 'dayjs';
import Qs from 'qs';
import SWRVCache from 'swrv/dist/cache';
import { fetcherFn, IConfig, IKey, IResponse } from 'swrv/dist/types';
import useSWRV, { mutate as writeSWRVCache } from 'swrv/dist/use-swrv';
import {
  ActionTypeValue,
  ActivityAction,
  AdminGroup,
  AdminTheme,
  AdminUserInfo,
  AdpDocument,
  AnalysisStatus,
  Article,
  Comment,
  Contract,
  CreditGrant,
  CreditPeriod,
  CreditSummary,
  CreditUsage,
  CurrentUserResponse,
  DocType,
  FeaturedArticle,
  FeedSummaryResponse,
  FmeaGenerationResponse,
  FmeaProject,
  FmeaProjectsQuery,
  FmeaProjectsResponse,
  FmeaProjectTag,
  FmeaSystemsQuery,
  FmeaSystemsResponse,
  FollowListMarkSortType,
  FollowListType,
  ForcedIdeationJob,
  ForcedIdeationJobListItem,
  GetSolutionDocumentsParams,
  Group,
  GroupActivityNewsCategories,
  GroupActivitySortType,
  GroupTagSortTypes,
  GroupTagWithCount,
  GroupUser,
  Industry,
  IndustryArticle,
  InsightRecommendedActionPromptPrefillStatus,
  InsightResponse,
  InsightSources,
  InsightStatus,
  IpAddressRestriction,
  JobType,
  KeywordCompletionRequest,
  KeywordCompletionResponse,
  KeywordRecommendResponse,
  Lang,
  MailDeliverySchedule,
  MarketSizingJob,
  MarketSizingJobListItem,
  MemberActivity,
  MSTeamsSettings,
  MyTagSortTypes,
  MyTagWithCount,
  NetworkGraphEdges,
  NetworkGraphNodes,
  NewsCategoryWithCount,
  NotificationItem,
  NotificationType,
  NotificationUnviewedAndUnreadCounts,
  OrganizationTagDetail,
  OrganizationTagList,
  OrganizationTagUserInfo,
  PatentFtoAnalysisConditionResponse,
  PatentFtoAnalysisListItem,
  PatentFtoAnalysisResponse,
  PatentFtoFavoriteProject,
  PatentFtoProjectWithAnalysisCount,
  PatentFtoRemainingExecutionCountResponse,
  PersonalFeedSummaryRequest,
  PersonalNewsCounts,
  PersonalSurveyProfile,
  PersonalSurveyProfileOptions,
  Perspective,
  PromptTemplateCategory,
  PromptTemplateEntity,
  PromptTemplates,
  PublicationOffice,
  Relevance,
  SamlFederation,
  SearchResultResponse,
  SharedType,
  ShareSurvey,
  ShareSurveyUserOption,
  SurveySessionResponse,
  Tag,
  TeamInfo,
  TechExploration,
  TechExplorationCollaboratorsResponse,
  TechExplorationComparisonResponse,
  TechExplorationHypothesisMemo,
  TechExplorationListItem,
  TechExplorationMentionCandidatesResponse,
  TechExplorationMonitoringPerspective,
  TechExplorationMonitoringThemeJob,
  TechExplorationRelatedUserDocument,
  TechExplorationRemainingUsageCount,
  TechExplorationReviewCommentsResponse,
  TechExplorationReviewCommentTargetType,
  TechExplorationSolutionDocumentsResponse,
  TechExplorationSolutionMemo,
  TechExplorationUserPreferences,
  Theme,
  ThemeFeedPatent,
  ThemeFeedResearchPaper,
  ThemeQueryGenerationRequest,
  ThemeSummaryRequest,
  ThemeTopicCategory,
  ThemeUser,
  TorendingTopics,
  TrackingPageType,
  UserDocumentContract,
  UserDocumentUsage,
  UserEntity,
  UserEntityWithAddUserOptions,
  UserInfo,
  UserItemSearchPreference,
  UserMarkedArticleCounts,
  UserSettings,
  UserSettingsEmail,
  UserSettingsNews,
  UserSettingsNotification,
  UserSettingsProfile,
  ViewedArticle,
} from '@/types';
import { FileDownloadJob } from '@/types/dashboard';
import {
  FmeaComponentNodes,
  FmeaProjectRiskRank,
  FmeaSheet,
  FmeaSheetReference,
  FmeaSystem,
  FmeaSystemResource,
} from '@/types/fmea';
import { isAfternoonFeedEnabled } from './personalNews';
import { SiteCategory } from './siteCategories';
import { userSession } from './userSession';

export const STATES = {
  VALIDATING: 'VALIDATING',
  PENDING: 'PENDING',
  SUCCESS: 'SUCCESS',
  ERROR: 'ERROR',
  STALE_IF_ERROR: 'STALE_IF_ERROR',
} as const;

export type SwrState = (typeof STATES)[keyof typeof STATES] | 'idle';

const useSwrvState = (
  data: ReturnType<typeof useSWRV>['data'],
  error: ReturnType<typeof useSWRV>['error'],
  isValidating: ReturnType<typeof useSWRV>['isValidating'],
) => {
  const state = ref<SwrState>('idle');
  watchEffect(() => {
    if (data.value && isValidating.value) {
      state.value = STATES.VALIDATING;
      return;
    }
    if (data.value && error.value) {
      state.value = STATES.STALE_IF_ERROR;
      return;
    }
    if (data.value === undefined && !error.value) {
      state.value = STATES.PENDING;
      return;
    }
    if (data.value && !error.value) {
      state.value = STATES.SUCCESS;
      return;
    }
    if (data.value === undefined && error.value) {
      state.value = STATES.ERROR;
      return;
    }
  });

  return {
    state,
  };
};

const SWRV_CACHE_KEYS = new Set<string>();

// eslint-disable-next-line @typescript-eslint/no-explicit-any
class ClearableSWRVCache extends SWRVCache<any> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  set(k: string, v: any, ttl: number): void {
    SWRV_CACHE_KEYS.add(k);
    super.set(k, v, ttl);
  }
}

const SWRV_OPTIONS = {
  refreshInterval: 0,
  ttl: 0,
  shouldRetryOnError: false,
  revalidateOnFocus: false,
  cache: new ClearableSWRVCache(),
} satisfies IConfig;

const DEFAULT_PAGE_LIMIT = 10;

const fetcher = async (url: string) => {
  const { data } = await axios.get(url);
  return data;
};

export const clearSWRVCache = () => {
  SWRV_CACHE_KEYS.forEach(key => {
    SWRV_OPTIONS.cache.delete(key);
    nonStickySwrvCache.delete(key);
  });
  SWRV_CACHE_KEYS.clear();
};

export const useSWRVWithState = <T>(key: IKey, configOptions?: IConfig) => {
  const config = {
    ...SWRV_OPTIONS,
    ...configOptions,
  };
  const { fetcher: overrideFetcher, ...restConfig } = config;
  const swrvData = useSWRV<T, AxiosError>(
    key,
    overrideFetcher ?? fetcher,
    restConfig,
  );
  const state = useSwrvState(
    swrvData.data,
    swrvData.error,
    swrvData.isValidating,
  );
  return { ...swrvData, ...state };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const useSWRVPostWithState = <T, Data = any>(
  url: string,
  payload: Ref<Data>,
  hasData: (payload: Ref<Data>) => boolean,
) => {
  const key = () =>
    hasData(payload)
      ? axios.getUri({ url: url, params: payload.value })
      : undefined;
  const fetcher = async <T>() => {
    const { data } = await axios.post<T>(url, payload.value);
    return data;
  };
  const swrvData = useSWRV<T, AxiosError>(key, fetcher, SWRV_OPTIONS);
  const state = useSwrvState(
    swrvData.data,
    swrvData.error,
    swrvData.isValidating,
  );
  return { ...swrvData, ...state };
};

// 前回の別のキーのときの値を返さないSWRVのラッパー
// 参考: https://zenn.dev/visasq/articles/difference-between-swr-and-swrv
const nonStickySwrvCache = new ClearableSWRVCache();
/**
 * swrv では fetcher の null が「取得しない」の意味だが、ここでは既定の fetcher に置き換わってしまうため、
 * fetcher 関数だけを受け取る。戻り値は既存の呼び出し元に合わせて useSWRV と同じ型にする。
 */
const useNonStickySWRV = <T>(
  keyFn: IKey,
  fn: fetcherFn<T> = fetcher,
): ReturnType<typeof useSWRV<T>> => {
  const { data, error, isValidating, mutate } = useSWRV<T, AxiosError>(
    keyFn,
    fn,
    {
      ...SWRV_OPTIONS,
      cache: nonStickySwrvCache,
    },
  );

  const patchedData =
    typeof keyFn === 'function'
      ? computed(() => {
          const value = data.value;
          const key = keyFn();
          if (
            !key ||
            (typeof key === 'string' && !nonStickySwrvCache.get(key))
          ) {
            return undefined;
          }
          return value;
        })
      : data;

  return {
    data: patchedData,
    error,
    isValidating,
    mutate,
  };
};

export const useNonStickySWRVWithState = <T>(keyFn: IKey) => {
  const swrvData = useNonStickySWRV<T>(keyFn);
  const state = useSwrvState(
    swrvData.data,
    swrvData.error,
    swrvData.isValidating,
  );
  return { ...swrvData, ...state };
};

export const useUserInfo = (userId?: Ref<number>) => {
  const uid = userId?.value ?? userSession.getUserId();
  const key = () => `/users/${uid}?include_action_counts=false`;
  return useSWRVWithState<UserInfo>(key);
};

export const useCurrentUser = () => {
  const key = () => `/users/current_user`;
  return useSWRVWithState<CurrentUserResponse>(key, {
    // SWRV_OPTIONSでフォーカス時と定期の再取得を切っているため、鮮度はこの値だけで決まる。
    // 既定の2秒のままだと、再マウントのたびに取り直してしまう。
    dedupingInterval: 5 * 60 * 1000,
  });
};

export const useTargetUserInfo = (userId: Ref<number | undefined>) => {
  const key = () =>
    userId.value
      ? `/users/${userId.value}?include_action_counts=false`
      : undefined;
  return useSWRVWithState<UserInfo>(key);
};

export const useUserActionCount = (
  userId: Ref<number | string | undefined>,
  actionType: 'mark' | 'comment' | 'view',
) => {
  const key = () => {
    const params = {
      action_type: actionType,
      doc_types: ['article', 'research_paper', 'patent', 'report'],
    };
    return userId.value
      ? `/users/${userId.value}/action_count?${Qs.stringify(params, {
          arrayFormat: 'brackets',
        })}`
      : undefined;
  };
  return useSWRVWithState<{ count: number }>(key);
};

export const useUserPersonalNewsDocumentCounts = () => {
  const key = () =>
    `/users/${userSession.getUserId()}/personal_news_document_counts`;
  return useSWRVWithState<PersonalNewsCounts>(key);
};

export const useNotifications = (
  pageRef: Ref<number>,
  pageLimit: number = DEFAULT_PAGE_LIMIT,
  notificationTypes: readonly NotificationType[],
  unreadOnly = false,
  trackingData: {
    page: {
      name: string;
      url: string;
    };
  },
  order: 'desc' | 'asc' = 'desc',
) => {
  const userId = userSession.getUserId();
  const key = () => {
    const params = {
      page: pageRef.value,
      limit: pageLimit,
      order: order,
      notification_types: notificationTypes,
      unread_only: unreadOnly,
      tracking_data: trackingData,
    };
    return `/users/${userId}/notifications?${Qs.stringify(params, {
      arrayFormat: 'brackets',
    })}`;
  };
  return useSWRVWithState<{
    notifications: NotificationItem[];
  }>(key);
};

export const useNotificationsUnviewedUnreadCount = <
  T extends Record<string, readonly NotificationType[]>,
>(
  notificationGrouping: T,
  configOptions?: IConfig,
) => {
  const userId = userSession.getUserId();
  const key = `/users/${userId}/notifications/unviewed_unread_counts?${Qs.stringify(
    { notification_grouping: notificationGrouping },
  )}`;
  return useSWRVWithState<{ counts: NotificationUnviewedAndUnreadCounts<T> }>(
    key,
    configOptions,
  );
};

export const useThemeList = (feedType: Ref<string>) => {
  const userId = userSession.getUserId();
  const key = () => {
    const params = {
      feed_type: feedType?.value,
      include_keyword_feed_counts: false,
    };
    return `/users/${userId}/themes?${Qs.stringify(params, {
      arrayFormat: 'brackets',
    })}`;
  };
  return useSWRVWithState<{ themes: Theme[] }>(key);
};

export const useThemeKeywordFeedCounts = (feedType?: Ref<string>) => {
  const key = () => {
    const params = {
      feed_type: feedType?.value,
    };
    return `/themes/keyword_feed_counts?${Qs.stringify(params, {
      arrayFormat: 'brackets',
    })}`;
  };
  return useSWRVWithState<{
    theme_keyword_feed_counts: Record<string, number>;
  }>(key);
};

export const useThemeMemberCounts = () => {
  const key = '/themes/member_counts';
  return useSWRVWithState<{ theme_member_counts: Record<string, number> }>(key);
};

export const useThemePreviewDocuments = (
  params: Ref<{
    doc_types: DocType[];
    keywords: string[];
    limit: number;
    lang: 'ja' | 'en';
    entertainment_filter: boolean;
    site_exclusion_ids: number[];
    site_categories: SiteCategory[];
    include_alliance_media: boolean;
  }>,
) => {
  return useSWRVPostWithState<
    Omit<
      SearchResultResponse,
      'search_tracking_session_id' | 'expand_queries' | 'news_categories'
    >,
    UnwrapRef<typeof params>
  >(
    '/search/theme_preview',
    params,
    params => params.value.keywords.length > 0,
  );
};

export const useOnboardingDocuments = (
  params: Ref<
    | {
        doc_types: DocType[];
        keywords: string[];
        limit: number;
        lang: 'ja' | 'en';
        entertainment_filter: boolean;
        site_exclusion_ids: number[];
        site_categories: SiteCategory[];
        include_alliance_media: boolean;
      }
    | undefined
  >,
) => {
  return useSWRVPostWithState<
    Omit<
      SearchResultResponse,
      'search_tracking_session_id' | 'expand_queries' | 'news_categories'
    >,
    UnwrapRef<typeof params>
  >(
    '/search/onboarding_documents',
    params,
    params => params.value !== undefined,
  );
};

export const useGroups = () => {
  const userId = userSession.getUserId();
  const key = () =>
    `/users/${userId}/groups?include_unread_comment_count=false`;
  return useSWRVWithState<{ groups: Group[] }>(key);
};

export const useGroupUnreadActivityCounts = () => {
  const userId = userSession.getUserId();
  const key = () => `/users/${userId}/groups/unread_activity_counts`;
  return useSWRVWithState<{
    unread_activity_counts: Record<string, number>;
  }>(key);
};

export const useGroupActivities = (
  groupId: number | undefined,
  page: Ref<number>,
  offset: Ref<number>,
  limit: number,
  sort: GroupActivitySortType,
  tags: string[],
  existsMemo: boolean | undefined,
  fromDate: string | undefined,
  toDate: string | undefined,
  lang: Lang[],
  docTypes: DocType[],
  query: string | undefined,
  newsCategories: string[],
) => {
  const key = () => {
    if (groupId === undefined) return undefined;
    const params = {
      page: page.value,
      offset: offset.value,
      limit: limit,
      sort: sort,
      tags: tags.length > 0 ? tags : undefined,
      exists_memo: existsMemo,
      from_date: fromDate,
      to_date: toDate,
      lang: lang.length > 0 ? lang : undefined,
      doc_types: docTypes.length > 0 ? docTypes : undefined,
      query: query,
      news_categories: newsCategories,
    };
    return `/groups/${groupId}/activities?${Qs.stringify(params, {
      arrayFormat: 'brackets',
    })}`;
  };
  return useSWRVWithState<{
    activities: (AdpDocument | Comment)[];
    next: number | null;
    page: number;
    tracking_session_id: string | undefined;
  }>(key);
};

export const useTeamUsers = () => {
  const teamId = userSession.getTeamId();
  const key = () => `/teams/${teamId}/users`;
  return useSWRVWithState<{
    users: UserEntity[];
  }>(key);
};

export const useTeamUsersWithAddUserOptions = () => {
  const teamId = userSession.getTeamId();
  const key = () => `/teams/${teamId}/users/with_add_user_options`;
  return useSWRVWithState<{
    users: UserEntityWithAddUserOptions[];
  }>(key);
};

export const useTeamInfo = (configOption?: IConfig) => {
  const myTeamId = userSession.getTeamId();
  const key = () => `/teams/${myTeamId}`;
  return useSWRVWithState<TeamInfo>(key, configOption);
};

export const useGroupUsers = (groupId: Ref<number | undefined>) => {
  const key = () =>
    groupId.value ? `/groups/${groupId.value}/users` : undefined;
  return useSWRVWithState<{ group_users: GroupUser[] }>(key);
};

export const useGroupActivitiesCount = (
  groupId: Ref<number | undefined>,
  tags: Ref<string[]>,
  existsMemo: Ref<boolean | undefined>,
  fromDate: Ref<string | undefined>,
  toDate: Ref<string | undefined>,
  lang: ComputedRef<Lang[]>,
  docTypes: Ref<DocType[]>,
  query: Ref<string>,
  newsCategories: Ref<string[]>,
) => {
  const key = () => {
    if (groupId.value === undefined) return undefined;
    const params = {
      exists_memo: existsMemo.value,
      tags: tags.value.length > 0 ? tags.value : undefined,
      from_date: fromDate.value,
      to_date: toDate.value,
      lang: lang.value.length > 0 ? lang.value : undefined,
      doc_types: docTypes.value.length > 0 ? docTypes.value : undefined,
      query: query.value,
      news_categories: newsCategories.value,
    };
    return `/groups/${groupId.value}/activities_count?${Qs.stringify(params, {
      arrayFormat: 'brackets',
    })}`;
  };
  return useNonStickySWRV<{ count: number }>(key);
};

export const useMarkedArticleCountsMypage = (
  userId: Ref<string | number | undefined> | undefined,
  tags: Ref<string[]>,
  memoExists: Ref<boolean | undefined>,
  fromDate: Ref<Date | undefined>,
  toDate: Ref<Date | undefined>,
  lang: Ref<Lang[]>,
  docTypes: Ref<DocType[]>,
  query: Ref<string | undefined>,
  news_categories: Ref<string[]>,
) => {
  const key = () => {
    if (userId?.value === undefined) return undefined;
    const params = {
      memo_exists: memoExists.value,
      tags: tags.value.length > 0 ? tags.value : undefined,
      from_date: fromDate.value
        ? dayjs(fromDate.value).format('YYYY-MM-DD')
        : undefined,
      to_date: toDate.value
        ? dayjs(toDate.value).format('YYYY-MM-DD')
        : undefined,
      lang: lang.value.length > 0 ? lang.value : undefined,
      doc_types: docTypes.value.length > 0 ? docTypes.value : undefined,
      query: query.value,
      news_categories: news_categories.value,
    };
    return `/users/${userId.value}/marked_articles_total_count?${Qs.stringify(
      params,
      {
        arrayFormat: 'brackets',
      },
    )}`;
  };
  return useSWRVWithState<{ counts: UserMarkedArticleCounts }>(key);
};

// TODO: endpointの修正
export const useUserMarkedArticleCounts = (
  userId: Ref<string | number | undefined>,
  query: Ref<string | undefined>,
) => {
  const key = () => {
    if (userId?.value === undefined) return undefined;
    const params = {
      query: query.value,
    };
    return `/users/${userId.value}/marked_articles_total_count?${Qs.stringify(
      params,
      {
        arrayFormat: 'brackets',
      },
    )}`;
  };
  return useSWRVWithState<{ counts: UserMarkedArticleCounts }>(key);
};

export const useMarkedArticlesMypage = (
  pageRef: Ref<number>,
  offsetRef: Ref<number>,
  pageLimit: number = DEFAULT_PAGE_LIMIT,
  userId: Ref<string | number | undefined> | undefined,
  tags: Ref<string[]>,
  memoExists: Ref<boolean | undefined>,
  fromDate: Ref<Date | undefined>,
  toDate: Ref<Date | undefined>,
  lang: Ref<Lang[]>,
  docTypes: Ref<DocType[]>,
  query: Ref<string | undefined>,
  news_categories: Ref<string[]>,
) => {
  const key = () => {
    if (userId?.value === undefined) return undefined;
    const params = {
      page: pageRef.value,
      offset: offsetRef.value,
      limit: pageLimit,
      order: 'desc',
      memo_exists: memoExists.value,
      tags: tags.value.length > 0 ? tags.value : undefined,
      from_date: fromDate.value
        ? dayjs(fromDate.value).format('YYYY-MM-DD')
        : undefined,
      to_date: toDate.value
        ? dayjs(toDate.value).format('YYYY-MM-DD')
        : undefined,
      lang: lang.value.length > 0 ? lang.value : undefined,
      doc_types: docTypes.value.length > 0 ? docTypes.value : undefined,
      query: query.value,
      include_technical_literature: true,
      news_categories: news_categories.value,
    };
    return `/users/${userId.value}/marked_articles_recursive?${Qs.stringify(
      params,
      {
        arrayFormat: 'brackets',
      },
    )}`;
  };
  return useSWRVWithState<{
    marked_articles: AdpDocument[];
    next: number | null;
    page: number;
    tracking_session_id: string | undefined;
  }>(key);
};

export const useUserMarkedArticles = (
  pageRef: Ref<number>,
  offsetRef: Ref<number>,
  pageLimit: number = DEFAULT_PAGE_LIMIT,
  userId: Ref<string | number | undefined> | undefined,
  query: Ref<string | undefined>,
) => {
  const key = () => {
    if (userId?.value === undefined) return undefined;
    const params = {
      page: pageRef.value,
      offset: offsetRef.value,
      limit: pageLimit,
      order: 'desc',
      query: query.value,
      include_technical_literature: true,
    };
    return `/users/${userId.value}/marked_articles_recursive?${Qs.stringify(
      params,
      {
        arrayFormat: 'brackets',
      },
    )}`;
  };
  return useSWRVWithState<{
    marked_articles: Article[];
    next: number | null;
    page: number;
    tracking_session_id: string | undefined;
  }>(key);
};

export const useComments = (
  pageRef: Ref<number>,
  pageLimit: number = DEFAULT_PAGE_LIMIT,
) => {
  const userId = userSession.getUserId();
  const key = () => {
    const params = {
      page: pageRef.value,
      limit: pageLimit,
      order: 'desc',
      doc_types: ['article', 'research_paper', 'patent', 'report'],
    };
    return `/users/${userId}/comments?${Qs.stringify(params, { arrayFormat: 'brackets' })}`;
  };
  return useSWRVWithState<{
    user_comments: Comment[];
  }>(key);
};

export const useGroupComments = (
  groupId: Ref<string>,
  pageRef: Ref<number>,
  pageLimit: number = DEFAULT_PAGE_LIMIT,
) => {
  const key = () => {
    if (groupId.value === undefined) return undefined;
    const params = {
      page: pageRef.value,
      limit: pageLimit,
      order: 'desc',
      doc_types: ['article', 'research_paper', 'patent', 'report'],
    };
    return `/groups/${groupId.value}/comments?${Qs.stringify(params, {
      arrayFormat: 'brackets',
    })}`;
  };
  return useSWRVWithState<{ group_comments: Comment[] }>(key);
};

export const useGroupMarks = (
  groupId: Ref<string | undefined>,
  pageRef: Ref<number>,
  pageLimit: number = DEFAULT_PAGE_LIMIT,
  memoExists: Ref<string>,
  tags: Ref<{ items: string[] }>,
) => {
  const key = () => {
    if (groupId.value === undefined) return null;

    const params = {
      page: pageRef.value,
      limit: pageLimit,
      order: 'desc',
      memo_exists: memoExists.value !== 'all' ? memoExists.value : undefined,
      tags: tags.value.items.length > 0 ? tags.value.items : undefined,
      include_technical_literature: true,
    };
    return `/groups/${groupId.value}/marked_articles?${Qs.stringify(params, {
      arrayFormat: 'brackets',
    })}`;
  };
  return useSWRVWithState<{ marked_articles: AdpDocument[]; count: number }>(
    key,
  );
};

export const useGroupMarksCount = (
  groupId: Ref<string | undefined>,
  memoExists: Ref<string>,
  tags: Ref<{ items: string[] }>,
) => {
  const key = () => {
    if (groupId.value === undefined) return null;

    const params = {
      exists_memo: memoExists.value !== 'all' ? memoExists.value : undefined,
      tags: tags.value.items.length > 0 ? tags.value.items : undefined,
    };
    return `/groups/${groupId.value}/marked_articles/count?${Qs.stringify(
      params,
      {
        arrayFormat: 'brackets',
      },
    )}`;
  };
  return useSWRVWithState<{ count: number }>(key);
};

export const useComment = (commentId: Ref<string>) => {
  const key = () => `/comments/${commentId.value}`;
  return useSWRVWithState<Comment>(key);
};

export const useDocument = (
  id: Ref<string | null>,
  doc_type: Ref<DocType | null>,
  params?: Ref<{
    article_lang?: string | undefined;
    patent_publication_office?: string | undefined;
    group_id_for_memo_and_tag?: number | undefined;
    report_page_number?: number | undefined;
  }>,
) => {
  const key = () => {
    if (!id.value || !doc_type.value) return null;

    return `/${doc_type.value}s/${id.value}?${Qs.stringify(params?.value)}`;
  };
  return useSWRVWithState<AdpDocument>(key);
};

export const useGroupTags = (
  groupId: ComputedRef<number | undefined | string>,
  actionType?: 'group_mark',
) => {
  const key = () => {
    if (!groupId.value) return null;
    const query = actionType
      ? `?${Qs.stringify({ action_type: actionType })}`
      : '';
    return `/groups/${groupId.value}/tags${query}`;
  };
  return useSWRVWithState<{ tags: Tag[] }>(key);
};

export const useGroupTagsNonSticky = (groupId: Ref<number | undefined>) => {
  const key = () => {
    if (!groupId.value) return null;
    return `/groups/${groupId.value}/tags`;
  };
  return useNonStickySWRV<{ tags: Tag[] }>(key);
};

export const useTeamFeaturedArticles = (
  activityAction: Ref<ActivityAction>,
  pageRef: Ref<number>,
  pageLimit: number = DEFAULT_PAGE_LIMIT,
) => {
  const teamId = userSession.getTeamId();
  const key = () => {
    const params = {
      activity_action: activityAction.value,
      limit: pageLimit,
      page: pageRef.value,
      order: 'desc',
    };
    return `/teams/${teamId}/featured_articles?${Qs.stringify(params)}`;
  };
  return useNonStickySWRVWithState<{
    featured_articles: FeaturedArticle[];
  }>(key);
};

export const useIndustryArticles = (
  industryId: Ref<number | undefined>,
  perTopicLimit?: number,
) => {
  const teamId = userSession.getTeamId();
  const key = () => {
    // industryIdがundefinedのときはswrvのkeyにnullを渡して
    // リクエストしないようにする
    if (industryId.value === undefined) return null;

    const params = {
      industry_id: industryId.value,
      per_topic_limit: perTopicLimit,
    };
    return `/teams/${teamId}/industry_articles?${Qs.stringify(params)}`;
  };

  return useSWRVWithState<{ industry_articles: IndustryArticle[] }>(key);
};

export const useIndustries = () => {
  const key = () => `/industries`;
  return useSWRVWithState<{ industries: Industry[] }>(key);
};

export const useUserOrganizationTags = (userId: Ref<number | undefined>) => {
  const key = () => {
    if (!userId.value) return null;
    return `/users/${userId.value}/organization_tags`;
  };
  return useSWRVWithState<{ organization_tags: OrganizationTagDetail[] }>(key);
};

export const useUserTags = () => {
  const userId = userSession.getUserId();
  const key = () => `/users/${userId}/tags`;
  return useSWRVWithState<{ user_tags: Tag[] }>(key);
};

export const useMailDeliverySchedules = () => {
  const teamId = userSession.getTeamId();
  const key = () => `/admin/teams/${teamId}/mail_delivery_schedules`;
  return useSWRVWithState<{ mail_delivery_schedules: MailDeliverySchedule[] }>(
    key,
  );
};

export const useMSTeamsSettings = () => {
  const teamId = userSession.getTeamId();
  const key = () => `/admin/teams/${teamId}/ms_teams_settings`;
  return useSWRVWithState<MSTeamsSettings>(key);
};

export const useThemeUsers = (themeId: number) => {
  const key = () => `/themes/${themeId}/users`;
  return useSWRVWithState<{ theme_users: ThemeUser[] }>(key);
};

export const usePersonalNewsMultipleExecution = (
  displayFeedType: Ref<string>,
  feedType: string,
  date: Dayjs,
  execution: string,
  page: Ref<number>,
  pageLimit: Ref<number> = ref(DEFAULT_PAGE_LIMIT),
) => {
  const userId = userSession.getUserId();
  const params = {
    feed_type: feedType,
    date: date.format('YYYY-MM-DD'),
    execution: execution,
    page: page.value,
    limit: pageLimit.value,
  };
  const key = (() =>
    (displayFeedType.value === 'all' || displayFeedType.value === feedType) &&
    (execution !== 'afternoon' || isAfternoonFeedEnabled(date))
      ? `/users/${userId}/personal_feeds?${Qs.stringify(params)}`
      : undefined) as IKey;
  return useSWRVWithState<{ personal_feeds: Article[]; count: number }>(key);
};

export const useThemeKeywordFeed = (
  themeId: number,
  feedType: Ref<string>,
  date: Ref<Date> | undefined,
  page: Ref<number> | undefined,
  pageLimit: number | undefined = DEFAULT_PAGE_LIMIT,
) => {
  const params = {
    feed_type: feedType.value,
    date: date ? dayjs(date.value).format('YYYY-MM-DD') : undefined,
    page: page ? page.value : undefined,
    limit: pageLimit,
  };
  const key = () => `/themes/${themeId}/keyword_feeds?${Qs.stringify(params)}`;
  return useSWRVWithState<{ keyword_feeds: Article[] }>(key);
};

export const useThemeRelatedFeed = (
  displayFeedType: Ref<string>,
  themeId: number,
  feedType: string,
  date: Ref<Date> | undefined,
  page: Ref<number> | undefined,
  pageLimit: number | undefined = DEFAULT_PAGE_LIMIT,
) => {
  const params = {
    feed_type: feedType,
    date: date ? dayjs(date.value).format('YYYY-MM-DD') : undefined,
    page: page ? page.value : undefined,
    limit: pageLimit,
  };
  const key = () =>
    displayFeedType.value === 'all' || displayFeedType.value === feedType
      ? `/themes/${themeId}/related_feeds?${Qs.stringify(params)}`
      : undefined;
  return useSWRVWithState<{ related_feeds: Article[] }>(key);
};

export const useThemeResearchPaperFeed = (
  displayFeedType: Ref<string>,
  themeId: number,
  feedType: string,
  page: Ref<number> | undefined,
  pageLimit: number | undefined = DEFAULT_PAGE_LIMIT,
) => {
  const params = {
    feed_type: feedType,
    page: page ? page.value : undefined,
    limit: pageLimit,
  };
  const key = (() =>
    displayFeedType.value === 'all' || displayFeedType.value === feedType
      ? `/themes/${themeId}/research_paper_feeds?${Qs.stringify(params)}`
      : undefined) as IKey;
  return useSWRVWithState<{ research_paper_feeds: ThemeFeedResearchPaper[] }>(
    key,
  );
};

export const useThemePatentFeed = (
  displayFeedType: Ref<string>,
  themeId: number,
  feedType: string,
  page: Ref<number> | undefined,
  pageLimit: number | undefined = DEFAULT_PAGE_LIMIT,
) => {
  const params = {
    feed_type: feedType,
    page: page ? page.value : undefined,
    limit: pageLimit,
  };
  const key = (() =>
    displayFeedType.value === 'all' || displayFeedType.value === feedType
      ? `/themes/${themeId}/patent_feeds?${Qs.stringify(params)}`
      : undefined) as IKey;
  return useSWRVWithState<{ patent_feeds: ThemeFeedPatent[] }>(key);
};

export const useTheme = (themeId: number) => {
  const key = () => `/themes/${themeId}`;
  return useSWRVWithState<{ themes: Theme }>(key);
};

export const useAdminGroups = () => {
  const teamId = userSession.getTeamId();
  const key = () => `/admin/teams/${teamId}/groups`;
  return useSWRVWithState<{ groups: AdminGroup[] }>(key);
};

export const useAdminUsers = () => {
  const teamId = userSession.getTeamId();
  const key = () => `/admin/teams/${teamId}/users`;
  return useSWRVWithState<{ users: AdminUserInfo[] }>(key);
};

export const useManagementContracts = (
  shouldFetch: ComputedRef<boolean> = computed(() => true),
) => {
  const key = () => (shouldFetch.value ? '/admin/management_contracts' : null);
  return useSWRVWithState<{ management_contracts: Contract[] }>(key);
};

export const useManagementContract = (contractId: Ref<number>) => {
  const key = () => {
    if (!contractId.value) return null;
    return `/admin/management_contracts/${contractId.value}`;
  };
  return useSWRVWithState<{ management_contract: Contract }>(key);
};

export const useManagementContractManagingUsers = (contractId: Ref<number>) => {
  const key = () => {
    if (!contractId.value) return null;
    return `/admin/management_contracts/${contractId.value}/managing_users`;
  };
  return useSWRVWithState<{ users: UserInfo[] }>(key);
};

export const useManagementContractCreditPeriods = (contractId: Ref<number>) => {
  const key = () => {
    if (!contractId.value) return null;
    return `/admin/management_contracts/${contractId.value}/credit_periods`;
  };
  return useSWRVWithState<CreditPeriod[]>(key);
};

export const useManagementContractCreditPeriod = (
  contractId: Ref<number>,
  creditPeriodId: Ref<number>,
) => {
  const key = () => {
    if (!contractId.value || !creditPeriodId.value) return null;
    return `/admin/management_contracts/${contractId.value}/credit_periods/${creditPeriodId.value}`;
  };
  return useSWRVWithState<CreditPeriod>(key);
};

export const useManagementContractCreditUsages = (
  contractId: Ref<number>,
  creditPeriodId: Ref<number>,
) => {
  const key = () => {
    if (!contractId.value || !creditPeriodId.value) return null;
    return `/admin/management_contracts/${contractId.value}/credit_periods/${creditPeriodId.value}/credit_usages`;
  };
  return useSWRVWithState<CreditUsage[]>(key);
};

export const useManagementContractCreditGrants = (
  contractId: Ref<number>,
  creditPeriodId: Ref<number>,
) => {
  const key = () => {
    if (!contractId.value || !creditPeriodId.value) return null;
    return `/admin/management_contracts/${contractId.value}/credit_periods/${creditPeriodId.value}/credit_grants`;
  };
  return useSWRVWithState<CreditGrant[]>(key);
};

export const useAdminSamlFederation = () => {
  const teamId = userSession.getTeamId();
  const key = () => `/admin/teams/${teamId}/saml_federation`;
  return useSWRVWithState<SamlFederation>(key);
};

export const useViewedArticles = (
  pageRef: Ref<number>,
  pageLimit: number = DEFAULT_PAGE_LIMIT,
) => {
  const userId = userSession.getUserId();
  const key = () => {
    const params = {
      page: pageRef.value,
      limit: pageLimit,
      include_technical_literature: true,
    };
    return `/users/${userId}/views?${Qs.stringify(params, {
      arrayFormat: 'brackets',
    })}`;
  };
  return useSWRVWithState<{ viewed_articles: ViewedArticle[] }>(key);
};

// NOTE: useUserSettingsをdeleteするまでの対応
// TODO: fix api endpoint
export const useUserSettingsEmail = () => {
  const userId = userSession.getUserId();
  const key = () => `/users/${userId}/settings`;
  return useSWRVWithState<UserSettingsEmail>(key);
};
export const useUserSettingsNotification = (
  endpoint: ComputedRef<string | undefined>,
) => {
  const userId = userSession.getUserId();
  // NOTE: nullは初期値。空文字はendpointがない場合
  const key = () =>
    `/users/${userId}/settings/notification${
      endpoint.value === undefined
        ? ''
        : `?${Qs.stringify({ endpoint: endpoint.value })}`
    }`;
  return useSWRVWithState<UserSettingsNotification>(key);
};
// TODO: fix api endpoint
// isEnabled が false の間は key を null にして fetch しない（呼び出し側が必要なときだけ取得できるようにするため）
export const useUserSettingsNews = (isEnabled: () => boolean = () => true) => {
  const userId = userSession.getUserId();
  const key = () => (isEnabled() ? `/users/${userId}/settings/` : null);
  return useSWRVWithState<UserSettingsNews>(key);
};
// TODO: fix api endpoint
export const useUserSettingsProfile = () => {
  const userId = userSession.getUserId();
  const key = () => `/users/${userId}/settings/`;
  return useSWRVWithState<UserSettingsProfile>(key);
};

export const useAdminThemes = () => {
  const teamId = userSession.getTeamId();
  const key = () => `/admin/teams/${teamId}/themes`;
  return useSWRVWithState<{ themes: AdminTheme[] }>(key);
};

export const useJobTypes = () => {
  const key = () => '/job_types';
  return useSWRVWithState<{ job_types: JobType[] }>(key);
};

export const useThemeTopicCategories = () => {
  const key = () => '/theme_topic_categories';
  return useSWRVWithState<{ theme_topic_categories: ThemeTopicCategory[] }>(
    key,
  );
};

export const useThemePerspectives = () => {
  const key = () => '/theme_perspectives';
  return useSWRVWithState<{ theme_perspectives: Perspective[] }>(key);
};

export const usePersonalResearchPaperFeed = (
  displayFeedType: Ref<string>,
  userId: number,
  feedType: string,
  page: Ref<number> | undefined,
  pageLimit: number | undefined = DEFAULT_PAGE_LIMIT,
) => {
  const params = {
    feed_type: feedType,
    page: page ? page.value : undefined,
    limit: pageLimit,
  };
  const key = (() =>
    displayFeedType.value === 'all' || displayFeedType.value === feedType
      ? `/users/${userId}/personal_research_paper_feeds?${Qs.stringify(params)}`
      : undefined) as IKey;
  return useSWRVWithState<{ research_paper_feeds: ThemeFeedResearchPaper[] }>(
    key,
  );
};

export const usePersonalPatentFeed = (
  displayFeedType: Ref<string>,
  userId: number,
  feedType: string,
  page: Ref<number> | undefined,
  pageLimit: number | undefined = DEFAULT_PAGE_LIMIT,
) => {
  const params = {
    feed_type: feedType,
    page: page ? page.value : undefined,
    limit: pageLimit,
  };
  const key = (() =>
    displayFeedType.value === 'all' || displayFeedType.value === feedType
      ? `/users/${userId}/personal_patent_feeds?${Qs.stringify(params)}`
      : undefined) as IKey;
  return useSWRVWithState<{ patent_feeds: ThemeFeedPatent[] }>(key);
};

export const useKeywordRecommend = (
  query: Ref<string>,
  minScore?: number,
): IResponse<KeywordRecommendResponse> => {
  const key = () => {
    if (query.value.trim() === '') return null;

    const param = new URLSearchParams();
    param.set('query', query.value);
    if (minScore) {
      param.set('min_score', String(minScore));
    }
    return `/keyword_recommendation?${param}`;
  };
  return useNonStickySWRV<KeywordRecommendResponse>(key);
};

export const useKeywordCompletion = (
  input: Ref<KeywordCompletionRequest>,
): IResponse<KeywordCompletionResponse> => {
  return useSWRVPostWithState<KeywordCompletionResponse>(
    '/keyword_completion',
    input,
    p => p.value.text.length > 0,
  );
};

export const useOrganizationTagDetail = (
  organizationTagId: number | Ref<number>,
) => {
  if (typeof organizationTagId === 'number') {
    return useSWRVWithState<OrganizationTagDetail>(
      `/organization_tags/${organizationTagId}`,
    );
  }
  const key = () => {
    return `/organization_tags/${organizationTagId.value}`;
  };
  return useSWRVWithState<OrganizationTagDetail>(key);
};

export const useOrganizationTagList = () => {
  const teamId = userSession.getTeamId();
  return useSWRVWithState<{
    organization_tags: OrganizationTagList;
  }>(`/organization_tags/teams/${teamId}`);
};

export const useOrganizationTagJoinableUsers = (organizationTagId: number) => {
  return useSWRVWithState<{
    users: OrganizationTagUserInfo[];
  }>(`/organization_tags/${organizationTagId}/joinable_users`);
};

export const useIpAddressRestrictions = () => {
  const teamId = userSession.getTeamId();
  return useSWRVWithState<{
    ip_address_restrictions: IpAddressRestriction[];
  }>(`/admin/teams/${teamId}/ip_address_restrictions`);
};

export const useFollowLists = () => {
  const key = () => `/follow_lists`;

  return useSWRVWithState<{ follow_lists: FollowListType[] }>(key);
};

export const useFollowList = (followListId: Ref<number>) => {
  const key = () => `/follow_lists/${followListId.value}`;

  return useSWRVWithState<FollowListType>(key);
};

export const useFollowListMarkedArticles = (
  followListId: Ref<number>,
  sort: Ref<FollowListMarkSortType | undefined>,
  page: Ref<number>,
  pageLimit: Ref<number> = ref(DEFAULT_PAGE_LIMIT),
  trackingData: TrackingPageType<'follow_list'>,
) => {
  const key = () => {
    if (sort.value === undefined) return null;

    const params = {
      page: page.value,
      limit: pageLimit.value,
      sort: sort.value,
      tracking_data: trackingData,

      // 呼び出し先のコンポーネントから渡されると、値が変わるたびにrefetchされてしまうため、自身でlocalStorageを参照する
      show_comment: userSession.getFollowListCommentsVisible(),
    };
    return `/follow_lists/${followListId.value}/marked_articles?${Qs.stringify(
      params,
      { arrayFormat: 'brackets' },
    )}`;
  };
  return useSWRVWithState<{ marked_articles: AdpDocument[] }>(key);
};

export const useNetworkGraph = (
  productContractIds: ComputedRef<string[]>,
  organizationTagIds: ComputedRef<number[]>,
) => {
  const key = () => {
    const params = {
      product_contract_ids: productContractIds.value,
      organization_tag_ids: organizationTagIds.value,
      type: 'member',
    };
    return `/labs/network_graph?${Qs.stringify(params, { arrayFormat: 'brackets' })}`;
  };
  return useSWRVWithState<{
    nodes: NetworkGraphNodes[];
    edges: NetworkGraphEdges[];
  }>(key);
};

export const useNetworkGraphOrganizationTag = (
  organizationTagNames: Ref<string[]>,
  from: Ref<Date>,
  to: Ref<Date>,
) => {
  const key = () => {
    const params = {
      from: from.value,
      to: to.value,
      organization_tag_names: organizationTagNames.value,
      type: 'organization_tag',
    };
    return `/labs/network_graph?${Qs.stringify(params, { arrayFormat: 'brackets' })}`;
  };
  return useSWRVWithState<{
    nodes: NetworkGraphNodes[];
    edges: NetworkGraphEdges[];
  }>(key);
};

export const useTrendingTopics = (
  organizationTagIds: Ref<number[]>,
  actionType: Ref<ActionTypeValue[]>,
  from: Ref<Date>,
  to: Ref<Date>,
) => {
  const key = () => {
    const params = {
      organization_tag_ids: organizationTagIds.value,
      action_type: actionType.value,
      from: from.value,
      to: to.value,
    };
    return `/labs/trending_topics?${Qs.stringify(params, { arrayFormat: 'brackets' })}`;
  };
  return useSWRVWithState<{
    solutions: TorendingTopics[];
    companies: TorendingTopics[];
  }>(key);
};

export const useAdpDocumentByIds = (ids: Ref<number[]>) => {
  const key = () => {
    if (ids.value.length === 0) return null;
    return `/articles/collection?${Qs.stringify({ ids: ids.value }, { arrayFormat: 'brackets' })}`;
  };
  return useSWRVWithState<AdpDocument[]>(key);
};

export const useUsers = () => {
  const myTeamId = userSession.getTeamId();
  const key = () => (myTeamId ? `/teams/${myTeamId}/users` : null);
  return useSWRVWithState<{ users: UserEntity[] }>(key);
};

export const useUserSettings = (userId: ComputedRef<number | undefined>) => {
  const key = () => (userId.value ? `/users/${userId.value}/settings` : null);
  return useSWRVWithState<UserSettings>(key);
};

export const useGroupTagTotalCount = (
  groupId: ComputedRef<number | undefined>,
) => {
  const key = () =>
    groupId.value ? `/groups/${groupId.value}/group_tag_count` : null;
  return useSWRVWithState<{ group_tag_count: number }>(key);
};

export const useMyTagTotalCount = (userId: ComputedRef<number | undefined>) => {
  const key = () => `/users/${userId.value}/my_tag_count`;
  return useSWRVWithState<{ my_tag_count: number }>(key);
};

export const useGroupTagItems = (
  groupId: ComputedRef<number | undefined>,
  sortBy?: Ref<GroupTagSortTypes | undefined>,
  pageRef?: Ref<number>,
  pageLimit: number = DEFAULT_PAGE_LIMIT,
) => {
  const key = () => {
    if (groupId.value === undefined) return null;

    return `/groups/${groupId.value}/group_tags?${Qs.stringify({
      sort_by: sortBy?.value,
      page: pageRef?.value,
      limit: pageLimit,
    })}`;
  };
  return useSWRVWithState<Array<GroupTagWithCount>>(key);
};

export const useGroupActivityNewsCategories = (
  groupId: Ref<number | undefined> | ComputedRef<number | undefined>,
) => {
  const key = () =>
    groupId.value ? `/groups/${groupId.value}/activity_news_categories` : null;
  return useSWRVWithState<{
    activity_news_categories: GroupActivityNewsCategories;
  }>(key);
};

export const useMypageNewsCategories = () => {
  return useSWRVWithState<{
    marked_articles_news_categories: NewsCategoryWithCount[];
  }>('/users/marked_articles_news_categories');
};

export const useMyTagItems = (
  sortBy: Ref<MyTagSortTypes | undefined>,
  pageRef: Ref<number>,
  pageLimit: number = DEFAULT_PAGE_LIMIT,
) => {
  const userId = userSession.getUserId();
  if (!userId) throw new Error('userId is not defined');

  const key = () =>
    `/users/${userId}/my_tags?${Qs.stringify({
      sort_by: sortBy.value,
      user_id: userId,
      page: pageRef.value,
      limit: pageLimit,
    })}`;
  return useSWRVWithState<Array<MyTagWithCount>>(key);
};

export const useShareSurveys = (
  keyword: Ref<string | undefined>,
  ownerId: Ref<number | undefined>,
  sampleOnly: Ref<boolean>,
  excludeOwner: Ref<boolean>,
  shared: Ref<SharedType | undefined>,
  startDate: Ref<Date | undefined>,
  endDate: Ref<Date | undefined>,
  pageRef: Ref<number>,
  pageLimit: number = DEFAULT_PAGE_LIMIT,
) => {
  const key = () => {
    if (ownerId.value === undefined) return null;

    return `/share_surveys?${Qs.stringify({
      keyword: keyword.value,
      owner_id: ownerId.value,
      sample_only: sampleOnly.value,
      exclude_owner: excludeOwner.value,
      shared: shared.value,
      start_date: startDate.value
        ? dayjs(startDate.value).format('YYYY-MM-DD')
        : undefined,
      end_date: endDate.value
        ? dayjs(endDate.value).format('YYYY-MM-DD')
        : undefined,
      page: pageRef.value,
      per_page: pageLimit,
    })}`;
  };
  return useSWRVWithState<{
    share_surveys: Array<ShareSurvey>;
    total_count: number;
  }>(key);
};

export const useSurveys = (
  keyword: Ref<string | undefined>,
  startDate: Ref<Date | undefined>,
  endDate: Ref<Date | undefined>,
  pageRef: Ref<number>,
  pageLimit: number = DEFAULT_PAGE_LIMIT,
) => {
  const key = () =>
    `/surveys/search?${Qs.stringify({
      keyword: keyword.value,
      start_date: startDate.value
        ? dayjs(startDate.value).format('YYYY-MM-DD')
        : undefined,
      end_date: endDate.value
        ? dayjs(endDate.value).format('YYYY-MM-DD')
        : undefined,
      page: pageRef.value,
      limit: pageLimit,
    })}`;
  return useSWRVWithState<{
    surveys: Array<SurveySessionResponse>;
    total_count: number;
  }>(key);
};

export const useUsageFileDownloadList = () => {
  const key = '/dashboard/downloads';
  // 2分ごとにポーリングする
  const config: IConfig = {
    refreshInterval: 120 * 1000,
  };
  return useSWRVWithState<FileDownloadJob[]>(key, config);
};

export const useThemeQueryGenerationRequests = (
  // 現状は'updated_at'のみソートキーとして許可されているが、今後追加される可能性があるため引数にしておく
  sort: 'updated_at',
  order: 'asc' | 'desc',
) => {
  const key = () => {
    const params = {
      sort,
      order,
    };
    return `/theme_query_generation_requests?${Qs.stringify(params)}`;
  };
  return useSWRVWithState<ThemeQueryGenerationRequest[]>(key);
};

export const useThemeQueryGenerationRequestById = (id: Ref<number | null>) => {
  const key = () => {
    return id.value ? `/theme_query_generation_requests/${id.value}` : null;
  };
  return useSWRVWithState<ThemeQueryGenerationRequest>(key);
};

export const useSurveyPromptTemplates = (
  pageRef: Ref<number>,
  pageLimit: number = DEFAULT_PAGE_LIMIT,
) => {
  const key = () =>
    `/surveys/prompt_templates?${Qs.stringify({
      page: pageRef.value,
      limit: pageLimit,
    })}`;
  return useSWRVWithState<PromptTemplates>(key);
};

export const useSurveyPromptTemplateFavorites = () => {
  const key = () => `/surveys/prompt_templates/favorites `;
  return useSWRVWithState<Array<PromptTemplateEntity>>(key);
};

export const useSurveyPromptTemplateCategories = () => {
  const key = () => `/surveys/prompt_templates/categories `;
  return useSWRVWithState<Array<PromptTemplateCategory>>(key);
};

export const useShareSurveyUserOptions = (uuid: string) => {
  const key = `/share_surveys/${uuid}/share_survey_user_options`;
  return useSWRVWithState<Array<ShareSurveyUserOption>>(key);
};

export const useUserDocumentUsage = (
  shouldFetch: MaybeRef<boolean>,
  polling: boolean = true,
) => {
  const teamId = userSession.getTeamId();
  const key = () =>
    unref(shouldFetch) ? `/admin/teams/${teamId}/user_document_usage` : null;
  const config = polling
    ? {
        dedupingInterval: 10 * 60 * 1000, // 10分間隔
        revalidateDebounce: 10 * 60 * 1000,
      }
    : undefined;

  return useSWRVWithState<UserDocumentUsage>(key, config);
};

export const useActiveUserDocumentContract = (shouldFetch: Ref<boolean>) => {
  const teamId = userSession.getTeamId();
  const key = () =>
    shouldFetch.value
      ? `/admin/teams/${teamId}/active_user_document_contract`
      : null;
  return useSWRVWithState<UserDocumentContract>(key, {
    dedupingInterval: 10 * 60 * 1000, // 10分間隔
    revalidateDebounce: 10 * 60 * 1000,
  });
};

export const usePersonalSurveyProfile = (shouldFetch: ComputedRef<boolean>) => {
  const key = () => (shouldFetch.value ? '/personal_survey_profile' : null);
  return useSWRVWithState<PersonalSurveyProfile>(key);
};

export const usePersonalSurveyProfileOptions = () => {
  return useSWRVWithState<PersonalSurveyProfileOptions>(
    '/personal_survey_profile/options',
  );
};

export const usePatentFtoProjects = (
  page: Ref<number>,
  per: number = DEFAULT_PAGE_LIMIT,
  query: Ref<string> = ref(''),
  updatedFrom: Ref<Date | undefined> = ref(),
  updatedTo: Ref<Date | undefined> = ref(),
  includeAnalysisCount: Ref<boolean> = ref(false),
) => {
  const key = () => {
    const params: {
      page: number;
      per: number;
      query?: string;
      updated_from?: string;
      updated_to?: string;
      include_analysis_count?: boolean;
    } = {
      page: page.value,
      per: per,
      include_analysis_count: includeAnalysisCount.value,
    };
    if (query.value) params.query = query.value;
    if (updatedFrom.value)
      params.updated_from = updatedFrom.value
        ? dayjs(updatedFrom.value).format('YYYY-MM-DD')
        : undefined;
    if (updatedTo.value)
      params.updated_to = updatedTo.value
        ? dayjs(updatedTo.value).format('YYYY-MM-DD')
        : undefined;
    return `/patent_fto/projects?${Qs.stringify(params)}`;
  };
  return useSWRVWithState<{
    patent_fto_projects: PatentFtoProjectWithAnalysisCount[];
    total_count: number;
    has_next: boolean;
  }>(key);
};

export const usePatentFtoAnalyses = (options: {
  page: Ref<number>;
  per?: number;
  createdFrom?: Ref<Date | undefined>;
  createdTo?: Ref<Date | undefined>;
  projectId?: Ref<number | undefined>;
  status?: Array<AnalysisStatus>;
  sortBy?: 'created_at' | 'last_updated_at';
}) => {
  const {
    page,
    per = DEFAULT_PAGE_LIMIT,
    createdFrom = ref(),
    createdTo = ref(),
    projectId = ref(),
    status,
    sortBy,
  } = options;

  const key = () => {
    const params: {
      page: number;
      per: number;
      created_from?: string;
      created_to?: string;
      project_id?: number;
      status?: Array<AnalysisStatus>;
      sort_by?: 'created_at' | 'last_updated_at';
    } = {
      page: page.value,
      per,
      project_id: projectId.value,
      status,
      sort_by: sortBy,
    };
    if (createdFrom.value)
      params.created_from = dayjs(createdFrom.value).format('YYYY-MM-DD');
    if (createdTo.value)
      params.created_to = dayjs(createdTo.value).format('YYYY-MM-DD');
    return `/patent_fto/analyses?${Qs.stringify(params, { arrayFormat: 'brackets' })}`;
  };
  return useSWRVWithState<{
    patent_fto_analyses: PatentFtoAnalysisListItem[];
    total_count: number;
    has_next: boolean;
  }>(key);
};

export const useUserItemSearchPreference = (itemType: 'theme' | 'group') => {
  const key = () => `/users/user_item_search_preference/${itemType}`;
  return useSWRVWithState<{
    user_item_search_preference: UserItemSearchPreference | null;
  }>(key);
};

export const usePatentFtoAnalysesTotalCount = (
  projectId?: Ref<number | undefined>,
) => {
  const key = () => {
    const params = {
      project_id: projectId?.value,
    };
    return `/patent_fto/analyses/total_count?${Qs.stringify(params)}`;
  };
  return useSWRVWithState<{ total_count: number }>(key);
};

export const usePatentFtoFavoriteProjects = () => {
  const key = () => '/patent_fto/projects/favorites';
  return useSWRVWithState<{
    favorite_projects: PatentFtoFavoriteProject[];
  }>(key);
};

export const useTechExplorations = () => {
  const key = () => `/tech_explorations`;
  return useSWRVWithState<Array<TechExplorationListItem>>(key);
};

export const useTechExploration = (uuid: ComputedRef<string>) => {
  const key = () => (uuid.value ? `/tech_explorations/${uuid.value}` : null);
  return useSWRVWithState<TechExploration>(key);
};

export const useTechExplorationCollaborators = (uuid: ComputedRef<string>) => {
  const key = () =>
    uuid.value ? `/tech_explorations/${uuid.value}/collaborators` : null;
  return useSWRVWithState<TechExplorationCollaboratorsResponse>(key);
};

export const useTechExplorationMentionCandidates = (
  uuid: ComputedRef<string>,
) => {
  const key = () =>
    uuid.value
      ? `/tech_explorations/${uuid.value}/comments/mention_candidates`
      : null;
  return useSWRVWithState<TechExplorationMentionCandidatesResponse>(key);
};

export const useTechExplorationComparison = (uuid: ComputedRef<string>) => {
  const key = () =>
    uuid.value ? `/tech_explorations/${uuid.value}/comparison` : null;
  return useSWRVWithState<TechExplorationComparisonResponse>(key);
};

export const useTechExplorationReviewComments = ({
  uuid,
  targetTypes,
  targetId,
  sort,
}: {
  uuid: MaybeRef<string | undefined>;
  targetTypes: MaybeRef<
    readonly TechExplorationReviewCommentTargetType[] | undefined
  >;
  targetId?: MaybeRef<number | undefined>;
  sort?: MaybeRef<('created_at_desc' | 'latest_comment_at_desc') | undefined>;
}) => {
  const key = () => {
    const resolvedUuid = unref(uuid);
    const resolvedTargetTypes = unref(targetTypes);
    const resolvedTargetId = unref(targetId);
    const resolvedSort = unref(sort);

    if (!resolvedUuid || !resolvedTargetTypes?.length) return null;

    const params = {
      target_types: resolvedTargetTypes,
      ...(resolvedTargetId === undefined
        ? {}
        : { target_id: resolvedTargetId }),
      ...(resolvedSort === undefined ? {} : { sort: resolvedSort }),
    };

    return `/tech_explorations/${resolvedUuid}/comments?${Qs.stringify(params, { arrayFormat: 'brackets' })}`;
  };

  return useSWRVWithState<TechExplorationReviewCommentsResponse>(key);
};

export const useTechExplorationMonitoringPerspectives = () => {
  const key = () => '/tech_exploration_monitoring_perspectives';
  return useSWRVWithState<TechExplorationMonitoringPerspective[]>(key);
};

export const useTechExplorationMonitoringThemeJob = (params: {
  uuid: string;
  jobId: Ref<number | null>;
}) => {
  const key = () =>
    params.jobId.value !== null
      ? `/tech_explorations/${params.uuid}/monitoring_themes/jobs/${params.jobId.value}`
      : null;
  return useSWRVWithState<TechExplorationMonitoringThemeJob>(key);
};

export const useTechExplorationSolutionDocuments = ({
  uuid,
  hypothesisId,
  solutionId,
  shouldFetch,
}: GetSolutionDocumentsParams) => {
  const key = () =>
    shouldFetch?.value === false
      ? null
      : `/tech_explorations/${uuid}/hypotheses/${hypothesisId}/solutions/${solutionId}/documents`;

  return useSWRVWithState<TechExplorationSolutionDocumentsResponse>(key);
};

export const usePatentFtoAnalysis = (
  analysisId: Ref<number>,
  page?: Ref<number>,
  per: MaybeRef<number> = DEFAULT_PAGE_LIMIT,
  patentResultFilter?: 'succeeded' | 'failed',
  relevances?: Ref<Relevance[] | undefined>,
  query?: Ref<string | undefined>,
  publicationOffices?: Ref<PublicationOffice[] | undefined>,
  sortBy?: Ref<'relevance' | undefined>,
  order?: Ref<'desc' | 'asc' | undefined>,
) => {
  const key = () => {
    const params = {
      page: page?.value,
      per: unref(per),
      patent_result_filter: patentResultFilter,
      relevances: relevances?.value,
      query: query?.value,
      publication_offices: publicationOffices?.value,
      sort_by: sortBy?.value,
      order: order?.value,
    };
    return `/patent_fto/analyses/${analysisId.value}?${Qs.stringify(params, { arrayFormat: 'brackets' })}`;
  };
  return useSWRVWithState<PatentFtoAnalysisResponse>(key);
};

export const usePatentFtoAnalysisCondition = (analysisId: Ref<number>) => {
  const key = () => `/patent_fto/analyses/${analysisId.value}/condition`;
  return useSWRVWithState<PatentFtoAnalysisConditionResponse>(key);
};

export const usePatentFtoRemainingExecutionCount = () => {
  const key = () => '/patent_fto/remaining_execution_count';
  return useSWRVWithState<PatentFtoRemainingExecutionCountResponse>(key);
};

export const usePatentFtoClaimResultVerifications = (
  analysisId: Ref<number>,
) => {
  const key = () =>
    `/patent_fto/analyses/${analysisId.value}/claim_result_verifications?adopted=true`;
  return useSWRVWithState<PatentFtoClaimResultVerificationListResponse>(key);
};

export const usePatentFtoRevisedInventionTheme = (analysisId: Ref<number>) => {
  const key = () =>
    `/patent_fto/analyses/${analysisId.value}/revised_invention_theme`;
  return useSWRVWithState<PatentFtoRevisedInventionTheme | null>(key, {
    fetcher: () => getRevisedInventionTheme(analysisId.value),
  });
};

export const useFeedSummariesPersonalFeed = (
  request: PersonalFeedSummaryRequest,
  enable: Ref<boolean>,
) => {
  const key = () => {
    if (!enable.value) return null;
    return `/feed_summaries/personal_feed?${Qs.stringify(request)}`;
  };

  return useSWRVWithState<FeedSummaryResponse>(key);
};

export const useFeedSummariesTheme = (
  request: ThemeSummaryRequest,
  enable: Ref<boolean>,
) => {
  const { theme_id, ...params } = request;
  const key = () => {
    if (!enable.value) return null;
    return `/feed_summaries/theme/${theme_id}?${Qs.stringify(params)}`;
  };

  return useSWRVWithState<FeedSummaryResponse>(key);
};

export const useTechExplorationRemainingUsageCount = () => {
  const key = () => 'tech_explorations/remaining_usage_count';
  return useSWRVWithState<TechExplorationRemainingUsageCount>(key);
};

export const useTechExplorationUserPreferences = () => {
  const key = () => '/tech_explorations/user_preferences';
  return useSWRVWithState<TechExplorationUserPreferences>(key);
};

export const useTechExplorationUserDocumentEnabled = (shouldFetch: boolean) => {
  if (!shouldFetch) {
    return {
      data: ref<{ enabled: boolean }>(),
    };
  }

  const key = () => '/tech_explorations/user_document_enabled';
  return useSWRVWithState<{ enabled: boolean }>(key);
};

export const useHypothesisRelatedUserDocuments = (
  uuid: string,
  hypothesisId: number,
  enabled: Ref<boolean>,
) => {
  const key = () =>
    enabled.value
      ? `/tech_explorations/${uuid}/hypotheses/${hypothesisId}/related_user_documents`
      : null;
  return useSWRVWithState<TechExplorationRelatedUserDocument[]>(key);
};

export const useSolutionRelatedUserDocuments = (
  uuid: string,
  hypothesisId: number,
  solutionId: number,
  enabled: Ref<boolean>,
) => {
  const key = () =>
    enabled.value
      ? `/tech_explorations/${uuid}/hypotheses/${hypothesisId}/solutions/${solutionId}/related_user_documents`
      : null;
  return useSWRVWithState<TechExplorationRelatedUserDocument[]>(key);
};

export const useHypothesisMemos = (uuid: string, hypothesisId: number) => {
  const key = `/tech_explorations/${uuid}/hypotheses/${hypothesisId}/memos`;
  return useSWRVWithState<TechExplorationHypothesisMemo[]>(key);
};

export const useSolutionMemos = (
  uuid: string,
  hypothesisId: number,
  solutionId: number,
) => {
  const key = `/tech_explorations/${uuid}/hypotheses/${hypothesisId}/solutions/${solutionId}/memos`;
  return useSWRVWithState<TechExplorationSolutionMemo[]>(key);
};

export const useCreditSummary = () => {
  const key = '/credit_summary';
  return useSWRVWithState<CreditSummary>(key);
};

export const useForcedIdeationJobs = () => {
  const key = '/forced_ideation';
  return useSWRVWithState<ForcedIdeationJobListItem[]>(key);
};

export const useForcedIdeationDrafts = () => {
  const key = '/forced_ideation/drafts';
  return useSWRVWithState<ForcedIdeationJobListItem[]>(key);
};

export const useForcedIdeationJob = (uuid: Ref<string | undefined>) => {
  // uuid 切り替え時に前のジョブの結果を返さないよう non-sticky にする
  // （sticky だと別ジョブの詳細に遷移した瞬間、前ジョブの結果が混ざる）
  const key = () => (uuid.value ? `/forced_ideation/${uuid.value}` : null);
  return useNonStickySWRV<ForcedIdeationJob>(key);
};

export const useMarketSizingJobs = () => {
  const key = '/market_sizing';
  return useSWRVWithState<MarketSizingJobListItem[]>(key);
};

export const useMarketSizingJob = (uuid: Ref<string | undefined>) => {
  // uuid 切り替え時に前のジョブの結果を返さないよう non-sticky にする
  // （sticky だと別ジョブの詳細に遷移した瞬間、前ジョブの結果が混ざる）
  const key = () => (uuid.value ? `/market_sizing/${uuid.value}` : null);
  return useNonStickySWRV<MarketSizingJob>(key);
};

export const useInsights = (enabled: Ref<boolean>) => {
  const key = () => (enabled.value ? '/insights' : null);
  return useSWRVWithState<InsightResponse>(key);
};

const INSIGHT_STATUS_KEY = '/insights/status';

export const useInsightStatus = (enabled: Ref<boolean>) => {
  const key = () => (enabled.value ? INSIGHT_STATUS_KEY : null);
  return useSWRVWithState<InsightStatus>(key);
};

export const useInsightRecommendedActionPrefillPromptStatuses = (
  enabled: Ref<boolean>,
) => {
  const key = () =>
    enabled.value ? '/insights/prefill_prompt_statuses' : null;
  return useSWRVWithState<InsightRecommendedActionPromptPrefillStatus[]>(key);
};

export const useInsightSources = (enabled: Ref<boolean>) => {
  const key = () => (enabled.value ? '/insights/sources' : null);
  return useSWRVWithState<InsightSources>(key);
};

/**
 * 保存結果などを直接書き込むリソースでは、書き込みより前に始まった取得が後から返ってきても、
 * その古いデータで表示を上書きしないようにする。
 * また、フックの mutate はタブが隠れていると何もせず、取得中なら取得結果のほうを書き込むため、
 * 保存結果などを確実に反映したいときは setCache でキャッシュと表示中のフックへ直接書き込む。
 */
const createWriteGuardedResource = <T>() => {
  let sequence = 0;
  const writtenAt = new Map<string, number>();
  const guardedFetcher = async (key: string): Promise<T> => {
    const startedAt = ++sequence;
    const data = (await fetcher(key)) as T;
    const cached = nonStickySwrvCache.get(key)?.data?.data as T | undefined;
    return (writtenAt.get(key) ?? 0) > startedAt && cached ? cached : data;
  };
  return {
    use: (key: IKey) => useNonStickySWRV<T>(key, guardedFetcher),
    setCache: (key: string, data: T) => {
      writtenAt.set(key, ++sequence);
      return writeSWRVCache(key, data, nonStickySwrvCache);
    },
  };
};

const fmeaSystemKey = (systemId: number) => `/fmea/systems/${systemId}`;

const fmeaSystem = createWriteGuardedResource<FmeaSystem>();

export const useFmeaSystem = (systemId: Ref<number | undefined>) =>
  fmeaSystem.use(() =>
    systemId.value === undefined ? null : fmeaSystemKey(systemId.value),
  );

export const setFmeaSystemCache = (systemId: number, system: FmeaSystem) =>
  fmeaSystem.setCache(fmeaSystemKey(systemId), system);

const fmeaSheetKey = (systemId: number) => `/fmea/systems/${systemId}/sheet`;

const fmeaSheet = createWriteGuardedResource<FmeaSheet>();

export const useFmeaSheet = (systemId: Ref<number | undefined>) =>
  fmeaSheet.use(() =>
    systemId.value === undefined ? null : fmeaSheetKey(systemId.value),
  );

export const setFmeaSheetCache = (systemId: number, sheet: FmeaSheet) =>
  fmeaSheet.setCache(fmeaSheetKey(systemId), sheet);

export const useFmeaSheetReferences = (
  systemId: Ref<number | undefined>,
  enabled: Ref<boolean>,
) => {
  const key = () =>
    systemId.value === undefined || !enabled.value
      ? null
      : `/fmea/systems/${systemId.value}/sheet/references`;
  return useNonStickySWRV<FmeaSheetReference[]>(key);
};

const fmeaComponentNodesKey = (systemId: number) =>
  `/fmea/systems/${systemId}/component_nodes`;

const fmeaComponentNodes = createWriteGuardedResource<FmeaComponentNodes>();

export const useFmeaComponentNodes = (systemId: Ref<number | undefined>) =>
  fmeaComponentNodes.use(() =>
    systemId.value === undefined ? null : fmeaComponentNodesKey(systemId.value),
  );

export const setFmeaComponentNodesCache = (
  systemId: number,
  tree: FmeaComponentNodes,
) => fmeaComponentNodes.setCache(fmeaComponentNodesKey(systemId), tree);

const fmeaProjectRiskCriteriaKey = (projectId: number) =>
  `/fmea/projects/${projectId}/risk_criteria`;

const fmeaProjectRiskCriteria =
  createWriteGuardedResource<FmeaProjectRiskRank[]>();

export const useFmeaProjectRiskCriteria = (
  projectId: Ref<number | undefined>,
) =>
  fmeaProjectRiskCriteria.use(() =>
    projectId.value === undefined
      ? null
      : fmeaProjectRiskCriteriaKey(projectId.value),
  );

export const setFmeaProjectRiskCriteriaCache = (
  projectId: number,
  ranks: FmeaProjectRiskRank[],
) =>
  fmeaProjectRiskCriteria.setCache(
    fmeaProjectRiskCriteriaKey(projectId),
    ranks,
  );

export const useMemberActivity = (limit: number = DEFAULT_PAGE_LIMIT) => {
  const teamId = userSession.getTeamId();
  const key = () => `/teams/${teamId}/member_activities?limit=${limit}`;
  return useSWRVWithState<{ activities: MemberActivity[] }>(key);
};

export const useFmeaProject = (id: MaybeRefOrGetter<number | undefined>) => {
  const key = () => {
    const projectId = toValue(id);
    return projectId === undefined ? undefined : `/fmea/projects/${projectId}`;
  };
  return useNonStickySWRV<FmeaProject>(key);
};

export const useFmeaProjects = (query: MaybeRefOrGetter<FmeaProjectsQuery>) => {
  const key = () =>
    `/fmea/projects?${Qs.stringify(toValue(query), { arrayFormat: 'brackets' })}`;
  return useSWRVWithState<FmeaProjectsResponse>(key, { dedupingInterval: 0 });
};

export const useFmeaSystems = (
  projectId: number,
  query: MaybeRefOrGetter<FmeaSystemsQuery>,
) => {
  const key = () =>
    `/fmea/projects/${projectId}/systems?${Qs.stringify(toValue(query))}`;
  return useSWRVWithState<FmeaSystemsResponse>(key, { dedupingInterval: 0 });
};

export const useFmeaSystemResources = (systemId: Ref<number | undefined>) => {
  const key = () =>
    systemId.value === undefined
      ? null
      : `/fmea/systems/${systemId.value}/resources`;
  return useNonStickySWRV<FmeaSystemResource[]>(key);
};

export const useFmeaProjectTags = (
  query: MaybeRefOrGetter<string> = '',
  limit: MaybeRefOrGetter<number | undefined> = undefined,
) => {
  const key = () =>
    `/fmea/project_tags?${Qs.stringify({ query: toValue(query), limit: toValue(limit) })}`;
  return useSWRVWithState<FmeaProjectTag[]>(key);
};

export const useFmeaGeneration = (systemId: Ref<number | undefined>) => {
  const key = () =>
    systemId.value === undefined
      ? null
      : `/fmea/systems/${systemId.value}/failure_mode_generations/latest`;
  return useNonStickySWRV<FmeaGenerationResponse>(key);
};
